// Neo's prompt version (cloud ADR 0332): `<label>+<fingerprint>`, such as
// `v61+3f9a2c1d`. The label is set by hand for a milestone; the fingerprint
// moves with every change to the prompt or the tools. Rows from before ADR
// 0332 hold a bare label (`v58`). The pages group rows by label and compare
// them by the exact version.

/** Between the label and the fingerprint. */
export const PROMPT_FINGERPRINT_SEPARATOR = '+';

/** A version's label: `v61` of `v61+3f9a2c1d`, and a bare `v58` as it is. */
export function promptLabelOf(version: string): string {
  const at = version.indexOf(PROMPT_FINGERPRINT_SEPARATOR);
  return at < 0 ? version : version.slice(0, at);
}

/** The rows of one label: one row per exact version, in the order given. */
export interface PromptLabelGroup<T> {
  /** Null for the rows that record no version. */
  label: string | null;
  rows: T[];
}

/**
 * Rows keyed by exact version, grouped under their label. A group comes where
 * its first row came, so rows sorted by size give groups led by their largest
 * version.
 */
export function groupByPromptLabel<T>(
  rows: readonly T[],
  versionOf: (row: T) => string | null
): PromptLabelGroup<T>[] {
  const groups = new Map<string | null, PromptLabelGroup<T>>();
  for (const row of rows) {
    const version = versionOf(row);
    const label = version === null ? null : promptLabelOf(version);
    const group = groups.get(label) ?? { label, rows: [] };
    group.rows.push(row);
    groups.set(label, group);
  }
  return [...groups.values()];
}
